// assetManagersData.js - Realistic Asset Manager Dashboard Data
// Portfolio: TechHub München + 2 other buildings in Munich
// Based on German commercial real estate market 2024-2025

/**
 * Get current language from localStorage
 */
function getCurrentLanguage() {
  return localStorage.getItem("lang") || "en";
}

/**
 * Format month display based on language
 */
function formatMonthDisplay(date) {
  const lang = getCurrentLanguage();
  return date.toLocaleDateString(lang === "de" ? "de-DE" : "en-US", {
    year: "numeric",
    month: "short"
  });
}

// Get language once at module load
const lang = getCurrentLanguage();
const isGerman = lang === "de";

export const assetManagersData = {
  // Portfolio Metadata
  portfolio: {
    id: 1,
    name: "TechHub Portfolio Munich",
    owner: "Munich Commercial Properties GmbH",
    location: "Munich, Bavaria, Germany",
    headquarters: "Maximilianstraße 45, 80539 München",
    established: 2015,

    // Portfolio Overview
    totalBuildings: 3,
    totalArea: 28500, // sqm total across 3 buildings
    totalValue: 285000000, // € 285M portfolio value
    totalUnits: 156, // Total leasable units

    buildings: [
      {
        id: 1,
        name: "TechHub München",
        address: "Leopoldstraße 252, 80807 München",
        yearBuilt: 2020,
        area: 10000, // sqm
        floors: 4,
        units: 52,
        type: isGerman ? "Gewerbebüro" : "Commercial Office",
        value: 95000000, // €95M
        purchaseYear: 2020,
        certifications: ["LEED Gold", "BREEAM Excellent"]
      },
      {
        id: 2,
        name: "Innovation Center Munich",
        address: "Arnulfstraße 160, 80634 München",
        yearBuilt: 2018,
        area: 12000, // sqm
        floors: 5,
        units: 68,
        type: isGerman ? "Gemischt genutzt" : "Mixed Use",
        value: 125000000, // €125M
        purchaseYear: 2019,
        certifications: ["LEED Platinum", "DGNB Gold"]
      },
      {
        id: 3,
        name: "Business Park Schwabing",
        address: "Ungererstraße 88, 80805 München",
        yearBuilt: 2016,
        area: 6500, // sqm
        floors: 3,
        units: 36,
        type: isGerman ? "Büropark" : "Office Park",
        value: 65000000, // €65M
        purchaseYear: 2021,
        certifications: ["BREEAM Very Good"]
      }
    ],

    // Management Info
    management: {
      ceo: "Dr. Michael Hoffmann",
      cfo: "Sandra Weber",
      assetManager: "Thomas Bauer",
      propertyManager: "Anna Schulze",
      sustainabilityOfficer: "Dr. Lisa Krämer",
      emergencyContact: "+49 89 2345 6789"
    },

    // Financial Structure
    financial: {
      currency: "EUR",
      fiscalYearEnd: "12-31",
      debtToEquity: 0.65, // 65% LTV typical for German commercial RE
      averageInterestRate: 3.8, // % current German commercial mortgage rates
      totalDebt: 185250000, // €185.25M (65% of value)
      equity: 99750000, // €99.75M
      reserveFund: 5700000 // €5.7M (2% of value)
    },

    // German Market Benchmarks (2024-2025)
    benchmarks: {
      occupancyRate: 90, // % target
      avgRentPerSqm: 25, // €/sqm/month for Munich commercial
      noiMargin: 68, // % Net Operating Income margin
      capRate: 4.2, // % typical Munich office cap rate
      tenantRetention: 92, // % target
      esgScore: 65, // ESG benchmark
      roiTarget: 8.5, // % annual ROI target
      collectionRate: 92, // % target rent collection
      vacancyCost: 15 // €/sqm/month during vacancy
    },

    // Operating Costs (German Standards)
    operatingCosts: {
      propertyManagement: 420000, // €420k annually (2% of rent)
      maintenance: 855000, // €855k annually (3% of value)
      utilities: 285000, // €285k annually (€10/sqm)
      insurance: 142500, // €142.5k annually (0.5% of value)
      propertyTax: 1425000, // €1.425M annually (5% of value)
      marketing: 142500, // €142.5k annually
      legal: 85500, // €85.5k annually
      other: 114000 // €114k annually
    }
  },

  // Monthly Data (22 months: January 2024 - October 2025)
  monthlyData: [
    // ==================== JANUARY 2024 ====================
    {
      month: "2024-01",
      displayMonth: formatMonthDisplay(new Date(2024, 0, 31)),
      date: "2024-01-31",

      // KPI 1: ESG Performance
      esg: {
        overallScore: 68.3,
        changePercent: 2.1,
        targetScore: 65,

        // ESG Components
        environmental: {
          score: 72,
          energyEfficiency: 78,
          waterManagement: 68,
          wasteManagement: 70,
          carbonFootprint: 72,
          greenCertifications: 3
        },

        social: {
          score: 66,
          tenantSatisfaction: 4.2,
          accessibility: 68,
          communityEngagement: 64,
          healthSafety: 70
        },

        governance: {
          score: 67,
          transparency: 72,
          compliance: 85,
          riskManagement: 65,
          stakeholderEngagement: 48
        },

        // Regional Performance (3 buildings)
        regional: {
          techHub: 72, // Best performer
          innovationCenter: 68,
          businessPark: 65
        },

        // Historical Trend (for charts)
        historicalTrend: [45, 48, 52, 56, 58, 62, 65, 68],

        // Certifications Status
        certifications: {
          leed: 2, // 2 buildings with LEED
          breeam: 3, // 3 buildings with BREEAM
          dgnb: 1, // 1 building with DGNB
          well: 0
        }
      },

      // KPI 2: Occupancy & Revenue
      occupancy: {
        overallRate: 89.6, // %
        changePercent: 1.8,
        targetRate: 90,

        // Revenue Metrics
        totalRevenue: 11833333, // €11.83M monthly (€142M annual / 12)
        potentialRevenue: 13207500, // €13.21M if 100% occupied
        collectionRate: 85.4, // %
        actualCollected: 10105467, // €10.1M actually collected
        efficiencyGap: 4.2, // % difference between occupancy and collection

        // Per Building
        buildings: [
          {
            id: 1,
            name: "TechHub München",
            occupancyRate: 92.3,
            units: 52,
            occupiedUnits: 48,
            revenue: 4166667, // €4.17M monthly
            avgRentPerSqm: 28 // €/sqm/month (premium)
          },
          {
            id: 2,
            name: isGerman ? "Innovationszentrum" : "Innovation Center",
            occupancyRate: 88.2,
            units: 68,
            occupiedUnits: 60,
            revenue: 5000000, // €5M monthly
            avgRentPerSqm: 25 // €/sqm/month
          },
          {
            id: 3,
            name: isGerman ? "Büropark" : "Business Park",
            occupancyRate: 88.9,
            units: 36,
            occupiedUnits: 32,
            revenue: 2666666, // €2.67M monthly
            avgRentPerSqm: 22 // €/sqm/month
          }
        ],

        // Lease Status
        leases: {
          active: 140,
          expiring3Months: 8,
          expiring6Months: 15,
          expiring12Months: 28,
          renewalRate: 87, // %
          avgLeaseLength: 48 // months (4 years)
        },

        // Vacancy Analysis
        vacancy: {
          rate: 10.4, // %
          vacantUnits: 16,
          avgVacancyPeriod: 42, // days
          costPerMonth: 427500 // €427.5k vacancy cost
        }
      },

      // KPI 3: Market Analysis & ROI
      marketRoi: {
        // Portfolio Performance
        portfolioValue: 285000000, // €285M
        acquisitionCost: 265000000, // €265M original
        appreciation: 20000000, // €20M appreciation
        appreciationPercent: 7.5,

        // ROI Metrics
        roi: 8.7, // % annual ROI
        capRate: 4.2, // % capitalization rate
        cashOnCash: 9.2, // % cash-on-cash return
        irr: 10.5, // % Internal Rate of Return

        // Annual Metrics
        annualRevenue: 142000000, // €142M
        noi: 96560000, // €96.56M (68% margin)
        noiMargin: 68, // %

        // Market Comparison
        marketYield: 4.2, // %
        marketAverage: 3.8, // %
        outperformance: 0.4, // % above market

        // Asset Distribution (by value)
        assetMix: {
          officeCommercial: 77, // % (€220M)
          mixedUse: 22, // % (€65M)
          other: 1 // %
        },

        // Value Drivers
        valuationFactors: {
          locationPremium: 15, // % premium for Munich location
          conditionScore: 92, // Building condition
          modernization: 88, // % modernized
          amenities: 85 // Amenities score
        }
      },

      // KPI 4: Tenant Experience
      tenantExperience: {
        // Satisfaction Metrics
        overallSatisfaction: 4.2, // out of 5
        changePercent: 2.8,
        targetSatisfaction: 4.0,

        // Retention & Renewals
        retentionRate: 94, // %
        renewalRate: 87, // %
        targetRetention: 92,

        // Response Metrics
        avgResponseTime: 21, // days for requests
        targetResponseTime: 30, // days
        maintenanceRequests: 42,
        completedRequests: 38,
        completionRate: 90.5, // %

        // Satisfaction Categories
        categories: {
          buildingCondition: 4.4,
          maintenance: 4.1,
          amenities: 4.3,
          security: 4.5,
          parking: 3.9,
          communication: 4.0,
          valueForMoney: 3.8
        },

        // Tenant Mix
        tenantTypes: {
          tech: 45, // %
          consulting: 24,
          finance: 15,
          creative: 11,
          other: 5
        },

        // Community Engagement
        engagement: {
          eventsHosted: 2,
          attendanceRate: 68,
          communityScore: 72,
          networkingOpportunities: 5
        }
      },

      // KPI 5: Financial Performance
      financial: {
        // Revenue Breakdown
        revenue: {
          baseRent: 11833333,
          serviceCharges: 1183333,
          parkingFees: 236667,
          other: 118333,
          total: 13371666
        },

        // Operating Costs
        costs: {
          propertyManagement: 35000,
          maintenance: 71250,
          utilities: 23750,
          insurance: 11875,
          propertyTax: 118750,
          marketing: 11875,
          legal: 7125,
          other: 9500,
          total: 289125
        },

        // Net Operating Income
        noi: 13082541,
        noiMargin: 68,
        noiChange: null,

        // Debt Service
        debtService: {
          principal: 154375,
          interest: 586875,
          total: 741250,
          coverage: 17.65
        },

        // Cash Flow
        cashFlow: {
          operatingCashFlow: 13082541,
          debtService: -741250,
          capex: -360000, // Capital expenditures
          netCashFlow: 11981291,
          distributionToOwners: 8985968
        },

        // Financial Ratios
        ratios: {
          operatingExpenseRatio: 28,
          debtServiceCoverage: 17.65,
          loanToValue: 65,
          returnOnEquity: 11.2,
          payoutRatio: 75
        }
      }
    },

    // ==================== FEBRUARY 2024 ====================
    {
      month: "2024-02",
      displayMonth: formatMonthDisplay(new Date(2024, 1, 29)),
      date: "2024-02-29",

      esg: {
        overallScore: 69.1,
        changePercent: 1.2,
        targetScore: 65,
        environmental: {
          score: 73,
          energyEfficiency: 79,
          waterManagement: 69,
          wasteManagement: 71,
          carbonFootprint: 73,
          greenCertifications: 3
        },
        social: {
          score: 67,
          tenantSatisfaction: 4.3,
          accessibility: 69,
          communityEngagement: 65,
          healthSafety: 71
        },
        governance: {
          score: 68,
          transparency: 73,
          compliance: 85,
          riskManagement: 66,
          stakeholderEngagement: 50
        },
        regional: { techHub: 73, innovationCenter: 69, businessPark: 66 },
        historicalTrend: [45, 48, 52, 56, 58, 62, 65, 68, 69],
        certifications: { leed: 2, breeam: 3, dgnb: 1, well: 0 }
      },

      occupancy: {
        overallRate: 90.4,
        changePercent: 0.9,
        targetRate: 90,
        totalRevenue: 11950000,
        potentialRevenue: 13207500,
        collectionRate: 86.2,
        actualCollected: 10301100,
        efficiencyGap: 3.7,
        buildings: [
          {
            id: 1,
            name: "TechHub München",
            occupancyRate: 92.3,
            units: 52,
            occupiedUnits: 48,
            revenue: 4166667,
            avgRentPerSqm: 28
          },
          {
            id: 2,
            name: isGerman ? "Innovationszentrum" : "Innovation Center",
            occupancyRate: 89.7,
            units: 68,
            occupiedUnits: 61,
            revenue: 5083333,
            avgRentPerSqm: 25
          },
          {
            id: 3,
            name: isGerman ? "Büropark" : "Business Park",
            occupancyRate: 88.9,
            units: 36,
            occupiedUnits: 32,
            revenue: 2700000,
            avgRentPerSqm: 22
          }
        ],
        leases: {
          active: 141,
          expiring3Months: 7,
          expiring6Months: 16,
          expiring12Months: 29,
          renewalRate: 88,
          avgLeaseLength: 48
        },
        vacancy: {
          rate: 9.6,
          vacantUnits: 15,
          avgVacancyPeriod: 38,
          costPerMonth: 410000
        }
      },

      marketRoi: {
        portfolioValue: 287250000,
        acquisitionCost: 265000000,
        appreciation: 22250000,
        appreciationPercent: 8.4,
        roi: 8.8,
        capRate: 4.3,
        cashOnCash: 9.4,
        irr: 10.6,
        annualRevenue: 143400000,
        noi: 97512000,
        noiMargin: 68,
        marketYield: 4.3,
        marketAverage: 3.8,
        outperformance: 0.5,
        assetMix: { officeCommercial: 77, mixedUse: 22, other: 1 },
        valuationFactors: {
          locationPremium: 15,
          conditionScore: 92,
          modernization: 88,
          amenities: 85
        }
      },

      tenantExperience: {
        overallSatisfaction: 4.3,
        changePercent: 2.4,
        targetSatisfaction: 4.0,
        retentionRate: 94,
        renewalRate: 88,
        targetRetention: 92,
        avgResponseTime: 19,
        targetResponseTime: 30,
        maintenanceRequests: 38,
        completedRequests: 36,
        completionRate: 94.7,
        categories: {
          buildingCondition: 4.5,
          maintenance: 4.2,
          amenities: 4.4,
          security: 4.5,
          parking: 4.0,
          communication: 4.1,
          valueForMoney: 3.9
        },
        tenantTypes: {
          tech: 46,
          consulting: 24,
          finance: 15,
          creative: 10,
          other: 5
        },
        engagement: {
          eventsHosted: 3,
          attendanceRate: 72,
          communityScore: 74,
          networkingOpportunities: 6
        }
      },

      financial: {
        revenue: {
          baseRent: 11950000,
          serviceCharges: 1195000,
          parkingFees: 239000,
          other: 119500,
          total: 13503500
        },
        costs: {
          propertyManagement: 35500,
          maintenance: 72000,
          utilities: 24000,
          insurance: 11875,
          propertyTax: 119500,
          marketing: 12000,
          legal: 7200,
          other: 9600,
          total: 291675
        },
        noi: 13211825,
        noiMargin: 68,
        noiChange: 1.0,
        debtService: {
          principal: 154375,
          interest: 586875,
          total: 741250,
          coverage: 17.82
        },
        cashFlow: {
          operatingCashFlow: 13211825,
          debtService: -741250,
          capex: -360000,
          netCashFlow: 12110575,
          distributionToOwners: 9082931
        },
        ratios: {
          operatingExpenseRatio: 28,
          debtServiceCoverage: 17.82,
          loanToValue: 65,
          returnOnEquity: 11.3,
          payoutRatio: 75
        }
      }
    }
    // Additional months would follow the same pattern with translations applied
  ],

  // Helper Methods
  getCurrentMonthData: function () {
    return this.monthlyData[this.monthlyData.length - 1];
  },

  getMonthData: function (monthIndex) {
    return this.monthlyData[monthIndex];
  },

  getHistoricalData: function (months = 12) {
    return this.monthlyData.slice(-months);
  }
};

// Export function to get data
export function getAssetManagersData() {
  // Auto-generate missing months up to current month
  const lastMonth =
    assetManagersData.monthlyData[assetManagersData.monthlyData.length - 1];
  const lastDate = new Date(lastMonth.date);
  const currentDate = new Date();

  // Generate missing months
  while (lastDate < currentDate) {
    lastDate.setMonth(lastDate.getMonth() + 1);

    if (lastDate <= currentDate) {
      const prevMonth =
        assetManagersData.monthlyData[assetManagersData.monthlyData.length - 1];

      // Generate new month with slight variations
      const newMonth = {
        month: lastDate.toISOString().substring(0, 7),
        displayMonth: formatMonthDisplay(lastDate),
        date: new Date(lastDate.getFullYear(), lastDate.getMonth() + 1, 0)
          .toISOString()
          .substring(0, 10),

        esg: {
          ...prevMonth.esg,
          overallScore: Math.min(
            85,
            prevMonth.esg.overallScore + (Math.random() * 2 - 0.5)
          ),
          changePercent: Math.random() * 3 - 1
        },

        occupancy: {
          ...prevMonth.occupancy,
          overallRate: Math.max(
            85,
            Math.min(
              95,
              prevMonth.occupancy.overallRate + (Math.random() * 2 - 1)
            )
          ),
          changePercent: Math.random() * 2 - 0.5,
          buildings: prevMonth.occupancy.buildings.map((building) => ({
            ...building,
            name:
              building.id === 2
                ? isGerman
                  ? "Innovationszentrum"
                  : "Innovation Center"
                : building.id === 3
                ? isGerman
                  ? "Büropark"
                  : "Business Park"
                : building.name
          }))
        },

        marketRoi: {
          ...prevMonth.marketRoi,
          portfolioValue:
            prevMonth.marketRoi.portfolioValue * (1 + Math.random() * 0.01),
          roi: Math.max(
            7,
            Math.min(12, prevMonth.marketRoi.roi + (Math.random() * 0.4 - 0.2))
          )
        },

        tenantExperience: {
          ...prevMonth.tenantExperience,
          overallSatisfaction: Math.max(
            3.5,
            Math.min(
              5,
              prevMonth.tenantExperience.overallSatisfaction +
                (Math.random() * 0.2 - 0.1)
            )
          ),
          changePercent: Math.random() * 3 - 1
        },

        financial: {
          ...prevMonth.financial,
          noi: prevMonth.financial.noi * (1 + (Math.random() * 0.02 - 0.01))
        }
      };

      assetManagersData.monthlyData.push(newMonth);
    }
  }

  return assetManagersData;
}
